import { Box, Stack, Typography } from '@dtx-company/design-system/src'
import { Plan, PlanPurchaseExperienceMode } from '@dtx-company/true-common/src/types/Plan'
import { PlanCostSummaryType } from '@dtx-company/inter-app/src/types/PlanCostSummary'
import { PlanPurchaseExperienceContext } from '../PlanPurchaseExperienceContext'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { getPlanTypeName } from '../../../utils/getPlanTypeName'
import { useCohortLimitsByPlan } from '@dtx-company/inter-app/src/hooks/cohorts/useCohortLimits'
import { useContext, useMemo } from 'react'
import { usePageLimit } from '@dtx-company/app/shared/page/src/components/flowpage/ClaimSlug/hooks/usePageLimit'

interface HeadingProps {
  selectedPlan: Plan | undefined
  mode?: PlanPurchaseExperienceMode
  /** The type of change that would be made if the users checks out with the selected plan
   * @see {@link PlanCostSummaryType} */
  planCostSummaryType?: PlanCostSummaryType
}

export function PlanPurchaseExperienceHeading(props: HeadingProps): JSX.Element {
  const { [PlanType.PRO]: pro } = useCohortLimitsByPlan({ isDefaultCohort: true })
  const proPageLimit = pro.pageLimit
  const context = useContext(PlanPurchaseExperienceContext)
  const planTypeName = getPlanTypeName(props.selectedPlan?.type || PlanType.PRO)
  const { pageLimit } = usePageLimit()

  const headingText = useMemo(() => {
    // Means the PPE was open thru a modal and messaging from the modal should be used
    if (context?.customHeadingText) return context.customHeadingText

    if (props.mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT) return 'Edit payment details'
    if (props.mode === PlanPurchaseExperienceMode.PURCHASE_ADDONS) return 'Build on your plan'

    if (context.isSwitchingBillingCycle) return 'Switch billing cycle'
    if (props.planCostSummaryType === 'DOWNGRADE') return "You've selected to downgrade your plan"
    return `Get more with Flowcode ${planTypeName}`
  }, [
    planTypeName,
    context?.customHeadingText,
    context.isSwitchingBillingCycle,
    props.mode,
    props.planCostSummaryType
  ])

  const subheadingText = useMemo(() => {
    const type = props.selectedPlan?.type
    if (type === PlanType.PRO) {
      if (pageLimit === 0) {
        return `More QR Codes (${proPageLimit}), more pages (${proPageLimit}), design capabilities and scan data`
      }

      return 'More QR codes, design capabilities, and scan data'
    }

    if (type === PlanType.PRO_PLUS_ORG) {
      return 'Get more codes, additional seats, and custom scan redirects'
    }
  }, [pageLimit, proPageLimit, props.selectedPlan?.type])

  return (
    <Stack
      sx={theme => ({
        textAlign: 'left',
        gap: theme.spacing(2)
      })}
    >
      <Typography component="h1" variant="headlineLarge">
        {headingText}
      </Typography>
      {subheadingText && (
        <Box sx={{ mt: 1 }}>
          <Typography component="h2" variant="subtitleSmall">
            {subheadingText}
          </Typography>
        </Box>
      )}
    </Stack>
  )
}
